import { useEffect, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks";
import { fetchBrandAssetsThunk } from "../../../stores/thunks/brandAssetsThunk";
import { updateCreatorBriefThunk } from "../../../stores/thunks/creatorBriefsThunk";
import { CreatorBrief } from "../../../stores/types/creatorBriefsTypes";
import {
  RevButton,
  RevCheckbox,
  RevDialog,
  RevLink,
  RevSpinner,
} from "@revmatics/catalyst";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  brief: CreatorBrief;
};

export const SelectBrandAssetsDialog = ({
  isOpen,
  setIsOpen,
  brief,
}: Props) => {
  const dispatch = useAppDispatch();

  const { assets, isLoading: isLoadingAssets } = useAppSelector(
    (state) => state.brandAssets,
  );
  const { isLoading: isUpdatingBrief } = useAppSelector(
    (state) => state.creatorBriefs,
  );

  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchBrandAssetsThunk());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (brief.brandassets) {
      const initialIds = brief.brandassets.map((asset) =>
        typeof asset === "number" ? asset : asset.id,
      );
      setSelectedAssetIds(initialIds);
    }
  }, [brief.brandassets]);

  const handleCheckboxChange = (assetId: number) => {
    setSelectedAssetIds((prevSelectedIds) =>
      prevSelectedIds.includes(assetId)
        ? prevSelectedIds.filter((id) => id !== assetId)
        : [...prevSelectedIds, assetId],
    );
  };

  const handleSubmit = useCallback(() => {
    const payload = { brandassets: selectedAssetIds };
    dispatch(updateCreatorBriefThunk({ id: brief.id, data: payload }))
      .unwrap()
      .then(() => {
        setIsOpen(false);
      });
  }, [dispatch, brief.id, selectedAssetIds, setIsOpen]);

  const dialogActions = [
    <RevButton
      key="cancel"
      text="Cancel"
      variant="secondary"
      handleClick={() => setIsOpen(false)}
    />,
    <RevButton
      key="submit"
      text="Submit"
      variant="primary"
      handleClick={handleSubmit}
      isLoading={isUpdatingBrief}
      disabled={isLoadingAssets || (assets && assets.length === 0)}
    />,
  ];

  const renderContent = () => {
    if (isLoadingAssets) {
      return <RevSpinner centered size="large" color="primary" />;
    }

    if (!assets || assets.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <p className="text-monochrome-500 mb-4">No Brand Assets</p>
          <RevLink
            pathname="/company-settings/"
            text="Manage Brand Assets"
            displayAs="button"
            buttonSize="small"
            buttonVariant="secondary"
          />
        </div>
      );
    }

    return (
      <div className="mb-12 lg:mb-0 overflow-y-auto scroll-vertical pr-2">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {assets.map((asset) => (
            <div
              key={asset.id}
              className="relative aspect-square border border-monochrome-200 rounded-lg overflow-hidden group"
            >
              {asset.image ? (
                <img
                  src={asset.image}
                  alt={asset.name || "Brand Asset"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-monochrome-50 flex items-center justify-center">
                  <p className="typography-body-xs text-monochrome-500">
                    No preview
                  </p>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-white p-2 border-t border-monochrome-200">
                <RevCheckbox
                  checked={selectedAssetIds.includes(asset.id)}
                  handleChecked={() => handleCheckboxChange(asset.id)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <RevDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Select Brand Assets"
      btnList={dialogActions}
      mobileSize="xs"
      size="m"
      heightClassName="h-3/4"
    >
      {renderContent()}
    </RevDialog>
  );
};
